import { useTranslation } from 'react-i18next';
import { User, TrendingUp, ShoppingBag } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AgentSummary } from '@/types';

interface AgentCardProps {
  agent: AgentSummary;
  isLoading?: boolean;
}

const AGENT_COLORS: Record<string, { bg: string; text: string; icon: string }> = {
  Tal: { bg: 'bg-blue-50', text: 'text-blue-700', icon: 'bg-blue-100' },
  May: { bg: 'bg-green-50', text: 'text-green-700', icon: 'bg-green-100' },
  June: { bg: 'bg-amber-50', text: 'text-amber-700', icon: 'bg-amber-100' },
  Elad: { bg: 'bg-purple-50', text: 'text-purple-700', icon: 'bg-purple-100' },
};

const DEFAULT_COLORS = { bg: 'bg-gray-50', text: 'text-gray-700', icon: 'bg-gray-100' };

export function AgentCard({ agent, isLoading }: AgentCardProps) {
  const { t, i18n } = useTranslation();

  const colors = AGENT_COLORS[agent.name] || DEFAULT_COLORS;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(i18n.language === 'he' ? 'he-IL' : 'en-US', {
      style: 'currency',
      currency: 'ILS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 animate-pulse">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
          <div className="h-5 bg-gray-200 rounded w-20"></div>
        </div>
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('rounded-xl shadow-sm border border-gray-200 p-4', colors.bg)}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className={cn('p-2 rounded-full', colors.icon)}>
          <User className={cn('w-5 h-5', colors.text)} />
        </div>
        <h3 className={cn('font-semibold text-lg', colors.text)}>{agent.name}</h3>
      </div>

      {/* Stats */}
      <div className="space-y-3">
        {/* Total Sales */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-600">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm">{t('agents.totalSales')}</span>
          </div>
          <span className="font-bold text-gray-900">{formatCurrency(agent.totalSales)}</span>
        </div>

        {/* Transaction Count */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-600">
            <ShoppingBag className="w-4 h-4" />
            <span className="text-sm">{t('agents.transactions')}</span>
          </div>
          <span className="font-bold text-gray-900">{agent.transactionCount}</span>
        </div>

        {/* Average Sale */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">{t('agents.averageSale')}</span>
          <span className="font-medium text-gray-700">
            {agent.transactionCount > 0
              ? formatCurrency(agent.totalSales / agent.transactionCount)
              : '-'}
          </span>
        </div>

        {/* Pending Transactions */}
        {agent.pendingCount > 0 && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-orange-600">{t('agents.pending')}</span>
              <span className="font-medium text-orange-600">{agent.pendingCount}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
